import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  GameState,
  Position,
  PieceType,
  IMove,
  MoveType,
} from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

interface TradeAnalysis {
  isTradeMove: boolean;
  isPureOffer: boolean;
  tradeValue: number;
  capturesEnemyKing: boolean;
  reasoning: string;
  shouldBlock: boolean;
}

/**
 * Priority 11: Trade Analysis
 *
 * Evaluates beneficial trades vs bad trades (when pieces ARE under attack).
 * NOTE: Pure hanging prevention is handled by Priority 10 (Hanging) to avoid duplication.
 *
 * Key Logic:
 * - King traps are ALWAYS encouraged (trades that capture enemy king)
 * - Filter out very bad trades (major material loss)
 * - Encourage strategic trades when behind in material
 * - Focus on trade VALUE, not pure hanging (that's already covered)
 */
export class TradePriority extends BaseDecisionPriority {
  readonly priority = 23;
  readonly name = "Trade Analysis";
  readonly description =
    "Evaluate beneficial vs bad trades (hanging handled separately)";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - need to check for trade opportunities
    // Note: Pure hanging is handled by Priority 10, this focuses on trade value
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const playerColor = gameState.isLightTurn ? "Light" : "Dark";

    this.addLog(
      `💰 TRADE ANALYSIS: Analyzing ${playerColor} trade opportunities...`
    );

    // NOTE: Pure hanging prevention is handled by Priority 10 (Hanging)
    // This priority focuses on BENEFICIAL TRADES vs BAD TRADES for pieces that are already under attack

    const kingTrapTrades: MoveInfo[] = [];
    const beneficialTrades: MoveInfo[] = [];
    const badTrades: MoveInfo[] = [];

    for (const moveInfo of context.allPossibleMoves) {
      const tradeAnalysis = this.analyzeTradeMove(moveInfo, gameState);

      // Only analyze moves that ARE trades (pieces under attack after move)
      if (!tradeAnalysis.isTradeMove) continue;

      if (tradeAnalysis.capturesEnemyKing) {
        kingTrapTrades.push(moveInfo);
        this.addLog(
          `👑 KING TRAP: ${this.formatMove(moveInfo)} - ${
            tradeAnalysis.reasoning
          }`
        );
      } else if (tradeAnalysis.tradeValue > 10) {
        beneficialTrades.push(moveInfo);
        this.addLog(
          `✅ GOOD TRADE: ${this.formatMove(
            moveInfo
          )} - Value: ${tradeAnalysis.tradeValue.toFixed(1)}`
        );
      } else if (tradeAnalysis.tradeValue < -20) {
        badTrades.push(moveInfo);
        this.addLog(
          `❌ BAD TRADE: ${this.formatMove(
            moveInfo
          )} - Value: ${tradeAnalysis.tradeValue.toFixed(1)}`
        );
      }
    }

    // PRIORITY 1: King trades - TACTICAL WIN!
    if (kingTrapTrades.length > 0) {
      const bestKingTrap = kingTrapTrades[0];

      // FINAL SAFETY CHECK: Double-verify this is not moving our own king
      const piece = getPieceFromPosition(gameState, bestKingTrap.fromPosition);
      if (piece && piece.isKing) {
        this.addLog(
          `🚨 CRITICAL ERROR: Trade priority attempted to sacrifice our own king! Move blocked!`
        );
        return {
          shouldEarlyExit: false,
          reasoning:
            "BLOCKED: Trade priority incorrectly identified king sacrifice as beneficial",
          priority: this.priority,
          diagnostics: { logs: this.analysisLogs },
        };
      }

      this.addLog(`🏆 KING TRADE AVAILABLE - Setting up tactical king trap!`);
      return this.createEarlyExitResult(
        bestKingTrap,
        "TACTICAL WIN: King trade setup - offering piece to enemy king!",
        {
          logs: this.analysisLogs,
          kingTraps: kingTrapTrades.length,
          tradeValue: 1000,
        }
      );
    }

    // PRIORITY 2: Filter out very bad trades (but let Hanging priority handle pure hanging)
    if (badTrades.length > 0) {
      const safeMoves = context.allPossibleMoves.filter(
        (move) =>
          !badTrades.some((badTrade) => this.areMovesEqual(move, badTrade))
      );

      if (safeMoves.length > 0) {
        this.addLog(
          `🛡️ Filtered ${badTrades.length} very bad trades, keeping ${safeMoves.length} moves`
        );

        return this.createFilterResult(
          safeMoves,
          `TRADE FILTER: Blocked ${badTrades.length} very bad trades`,
          {
            logs: this.analysisLogs,
            badTradesBlocked: badTrades.length,
            safeMoves: safeMoves.length,
          }
        );
      }
    }

    // PRIORITY 3: Encourage beneficial trades when strategically sound
    if (beneficialTrades.length > 0 && this.shouldEncourageTrades(gameState)) {
      this.addLog(
        `📈 STRATEGIC TRADES: Encouraging beneficial trades - ahead in material`
      );

      // Find the best trade
      const bestTrade = beneficialTrades.reduce((best, current) => {
        const bestAnalysis = this.analyzeTradeMove(best, gameState);
        const currentAnalysis = this.analyzeTradeMove(current, gameState);
        return currentAnalysis.tradeValue > bestAnalysis.tradeValue
          ? current
          : best;
      });

      const bestTradeAnalysis = this.analyzeTradeMove(bestTrade, gameState);

      return this.createEarlyExitResult(
        bestTrade,
        `STRATEGIC TRADE: Best value trade (${bestTradeAnalysis.tradeValue.toFixed(
          1
        )})`,
        {
          logs: this.analysisLogs,
          tradeValue: bestTradeAnalysis.tradeValue,
          totalTrades: beneficialTrades.length,
        }
      );
    }

    this.addLog(
      `ℹ️ No beneficial trade opportunities - normal move evaluation continues`
    );

    return {
      shouldEarlyExit: false,
      reasoning: "No beneficial trade opportunities detected",
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private analyzeTradeMove(
    moveInfo: MoveInfo,
    gameState: GameState
  ): TradeAnalysis {
    try {
      const piece = getPieceFromPosition(gameState, moveInfo.fromPosition);
      if (!piece) {
        return {
          isTradeMove: false,
          isPureOffer: false,
          tradeValue: 0,
          capturesEnemyKing: false,
          reasoning: "No piece found",
          shouldBlock: false,
        };
      }

      // CRITICAL FIX: NEVER allow our king to be offered as trade bait!
      // If our king is captured, the game ends immediately - no "trade back" possible!
      if (piece.isKing) {
        return {
          isTradeMove: false,
          isPureOffer: false,
          tradeValue: 0,
          capturesEnemyKing: false,
          reasoning:
            "King moves are NOT trade candidates - handled by King Safety priority",
          shouldBlock: false,
        };
      }

      // Simulate the move
      const testState = cloneFullGameState(gameState);
      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      const newPosition = moveInfo.move.position;

      // Check if piece is under attack after move
      const isUnderAttack = this.isPieceUnderAttack(testState, newPosition);

      if (!isUnderAttack) {
        return {
          isTradeMove: false,
          isPureOffer: false,
          tradeValue: 0,
          capturesEnemyKing: false,
          reasoning: "Move is safe - no trade involved",
          shouldBlock: false,
        };
      }

      // Piece is under attack - analyze trade potential
      const tradeBackAnalysis = this.analyzeTradeBackPotential(
        gameState,
        newPosition,
        moveInfo
      );

      // CRITICAL: King trade detection
      // This detects when we can offer a piece to the enemy king such that
      // if the enemy king captures our piece, we can capture the enemy king
      if (tradeBackAnalysis.capturesEnemyKing) {
        return {
          isTradeMove: true,
          isPureOffer: false,
          tradeValue: 1000,
          capturesEnemyKing: true,
          reasoning:
            "🏆 KING TRADE: Offering piece to enemy king - we can capture king if they take it!",
          shouldBlock: false,
        };
      }

      // Can't trade back - this is handled by Hanging priority, so just evaluate the trade
      if (!tradeBackAnalysis.canTradeBack) {
        return {
          isTradeMove: true,
          isPureOffer: false,
          tradeValue: -50, // Negative value but let Hanging priority handle blocking
          capturesEnemyKing: false,
          reasoning: "No recapture possible - bad trade value",
          shouldBlock: false, // Let Hanging priority handle this
        };
      }

      // Calculate trade value
      const tradeValue = this.evaluateTradeValue(
        gameState,
        testState,
        moveInfo
      );

      return {
        isTradeMove: true,
        isPureOffer: false,
        tradeValue: tradeValue,
        capturesEnemyKing: false,
        reasoning: `Trade move: ${
          tradeValue > 0 ? "Beneficial" : "Unfavorable"
        } (value: ${tradeValue.toFixed(1)})`,
        shouldBlock: tradeValue < -30, // Block very bad trades
      };
    } catch (error) {
      return {
        isTradeMove: false,
        isPureOffer: false,
        tradeValue: 0,
        capturesEnemyKing: false,
        reasoning: "Error in trade analysis",
        shouldBlock: false,
      };
    }
  }

  private isPieceUnderAttack(
    gameState: GameState,
    position: Position
  ): boolean {
    // Generate enemy moves and check if any target this position
    const enemyMoves =
      MCTSMoveGenerationService.getInstance().getAllMoves(gameState);

    return enemyMoves.some(
      (enemyMove) =>
        enemyMove.move.position.row === position.row &&
        enemyMove.move.position.col === position.col
    );
  }

  private analyzeTradeBackPotential(
    originalGameState: GameState,
    targetPosition: Position,
    originalMove: MoveInfo
  ): { canTradeBack: boolean; capturesEnemyKing: boolean } {
    try {
      // Find enemy pieces that can capture us at the target position
      const enemyCaptureMoves = MCTSMoveGenerationService.getInstance()
        .getAllMoves(originalGameState)
        .filter(
          (move) =>
            move.move.position.row === targetPosition.row &&
            move.move.position.col === targetPosition.col
        );

      if (enemyCaptureMoves.length === 0) {
        return { canTradeBack: false, capturesEnemyKing: false };
      }

      let canTradeBack = false;
      let isKingTrade = false;

      // Check each potential enemy that could capture us
      for (const enemyCapture of enemyCaptureMoves) {
        const enemyPiece = getPieceFromPosition(
          originalGameState,
          enemyCapture.fromPosition
        );
        if (!enemyPiece) continue;

        // Simulate enemy capturing our piece
        const postCaptureState = cloneFullGameState(originalGameState);
        const originalPlayerIsLight = originalGameState.isLightTurn;

        applyMoveWithSnakeRespawn(
          postCaptureState,
          enemyCapture.fromPosition,
          enemyCapture.move,
          enemyPiece
        );

        // CRITICAL FIX: Ensure we're analyzing moves for the ORIGINAL player (who wants to recapture)
        // After enemy captures, it should be our turn again
        const expectedTurnAfterCapture = originalPlayerIsLight;
        this.addLog(
          `🔄 TURN CHECK: Original player was ${
            originalPlayerIsLight ? "Light" : "Dark"
          }, post-capture turn is ${
            postCaptureState.isLightTurn ? "Light" : "Dark"
          }`
        );

        if (postCaptureState.isLightTurn !== expectedTurnAfterCapture) {
          this.addLog(
            `🚨 TURN MISMATCH: Expected ${
              expectedTurnAfterCapture ? "Light" : "Dark"
            } but got ${
              postCaptureState.isLightTurn ? "Light" : "Dark"
            } - correcting!`
          );
          postCaptureState.isLightTurn = expectedTurnAfterCapture;
        }

        // CRITICAL FIX: Check if we can actually CAPTURE the enemy piece that captured us
        const allOurMoves =
          MCTSMoveGenerationService.getInstance().getAllMoves(postCaptureState);
        const targetPosition = enemyCapture.move.position;

        this.addLog(
          `🔍 RECAPTURE ANALYSIS: Looking for ways to capture ${enemyPiece.type} at [${targetPosition.row},${targetPosition.col}]`
        );
        this.addLog(
          `📊 Total moves available: ${allOurMoves.length} (for ${
            postCaptureState.isLightTurn ? "Light" : "Dark"
          })`
        );

        const ourRecaptureMoves = allOurMoves.filter(
          (move) =>
            move.move.position.row === targetPosition.row &&
            move.move.position.col === targetPosition.col &&
            move.move.moveType === MoveType.capture // ONLY actual captures!
        );

        // DETAILED DEBUGGING: Show exactly which pieces the AI thinks can recapture
        for (const recaptureMove of ourRecaptureMoves) {
          const ourPiece = getPieceFromPosition(
            postCaptureState,
            recaptureMove.fromPosition
          );
          this.addLog(
            `⚔️ CLAIMED RECAPTURE: ${ourPiece?.type || "Unknown"} at [${
              recaptureMove.fromPosition.row
            },${recaptureMove.fromPosition.col}] claims it can CAPTURE at [${
              targetPosition.row
            },${targetPosition.col}]`
          );
        }

        // ALSO CHECK: Are there any moves to that position that are NOT captures?
        const nonCaptureMoves = allOurMoves.filter(
          (move) =>
            move.move.position.row === targetPosition.row &&
            move.move.position.col === targetPosition.col &&
            move.move.moveType !== MoveType.capture
        );

        if (nonCaptureMoves.length > 0) {
          this.addLog(
            `🚫 NON-CAPTURE MOVES: ${nonCaptureMoves.length} pieces can MOVE to [${targetPosition.row},${targetPosition.col}] but cannot CAPTURE`
          );
          for (const nonCaptureMove of nonCaptureMoves) {
            const ourPiece = getPieceFromPosition(
              postCaptureState,
              nonCaptureMove.fromPosition
            );
            this.addLog(
              `   - ${ourPiece?.type || "Unknown"} at [${
                nonCaptureMove.fromPosition.row
              },${nonCaptureMove.fromPosition.col}] (MoveType: ${
                nonCaptureMove.move.moveType
              })`
            );
          }
        }

        if (ourRecaptureMoves.length > 0) {
          canTradeBack = true;
          this.addLog(
            `✅ CLAIMED VALID 3-PLY TRADE: ${ourRecaptureMoves.length} pieces claim they can recapture ${enemyPiece.type}`
          );

          // KING TRADE DETECTION: If the enemy piece that could capture us is the enemy king,
          // and we can recapture, that's a king trade setup!
          if (enemyPiece.isKing) {
            isKingTrade = true;
          }
        } else {
          this.addLog(
            `❌ NO VALID RECAPTURE: Cannot actually capture ${enemyPiece.type} after it captures our piece`
          );
        }
      }

      return {
        canTradeBack,
        capturesEnemyKing: isKingTrade,
      };
    } catch (error) {
      return { canTradeBack: false, capturesEnemyKing: false };
    }
  }

  private evaluateTradeValue(
    originalState: GameState,
    postMoveState: GameState,
    moveInfo: MoveInfo
  ): number {
    // Simple material evaluation
    const originalMaterial = this.getMaterialCount(originalState);
    const postMoveMaterial = this.getMaterialCount(postMoveState);

    const ourColor = originalState.isLightTurn;
    const materialDiff = ourColor
      ? postMoveMaterial.light -
        originalMaterial.light -
        (postMoveMaterial.dark - originalMaterial.dark)
      : postMoveMaterial.dark -
        originalMaterial.dark -
        (postMoveMaterial.light - originalMaterial.light);

    return materialDiff * 10; // Scale for priority system
  }

  private getMaterialCount(gameState: GameState): {
    light: number;
    dark: number;
  } {
    let light = 0,
      dark = 0;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        for (const piece of gameState.board[row][col].Pieces) {
          const value = this.getPieceValue(piece.type);
          if (piece.isLight) {
            light += value;
          } else {
            dark += value;
          }
        }
      }
    }

    return { light, dark };
  }

  private getPieceValue(pieceType: PieceType): number {
    // All pieces equal value - no LLM assumptions about piece worth
    switch (pieceType) {
      case PieceType.Penguin:
        return 1;
      case PieceType.Crane:
        return 1;
      case PieceType.Goat:
        return 1;
      case PieceType.Monkey:
        return 1;
      case PieceType.Wolf:
        return 1;
      case PieceType.Frog:
        return 1;
      case PieceType.Snake:
        return 1;
      case PieceType.Bear:
        return 1;
      case PieceType.Rhino:
        return 1;
      case PieceType.Tree:
        return 1;
      case PieceType.Fungi:
        return 1;
      case PieceType.Octopus:
        return 1;
      default:
        return 1;
    }
  }

  private shouldEncourageTrades(gameState: GameState): boolean {
    // STRICT TRADE LOGIC: Only trade when significantly ahead
    const material = this.getMaterialCount(gameState);
    const ourColor = gameState.isLightTurn;

    const ourMaterial = ourColor ? material.light : material.dark;
    const enemyMaterial = ourColor ? material.dark : material.light;
    const materialAdvantage = ourMaterial - enemyMaterial;

    this.addLog(
      `📊 Material: Us ${ourMaterial} vs Enemy ${enemyMaterial} (Advantage: ${materialAdvantage})`
    );

    // VERY STRICT: Only trade when ahead by 5+ points (major piece advantage)
    if (materialAdvantage >= 5) {
      this.addLog(
        `✅ SIGNIFICANT material advantage - trades acceptable (${materialAdvantage} points ahead)`
      );
      return true;
    } else {
      this.addLog(
        `❌ INSUFFICIENT material advantage - avoid all trades (only ${materialAdvantage} points ahead, need 5+)`
      );
      return false;
    }
  }

  private formatMove(moveInfo: MoveInfo): string {
    return `[${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;
  }

  private areMovesEqual(move1: MoveInfo, move2: MoveInfo): boolean {
    return (
      move1.fromPosition.row === move2.fromPosition.row &&
      move1.fromPosition.col === move2.fromPosition.col &&
      move1.move.position.row === move2.move.position.row &&
      move1.move.position.col === move2.move.position.col
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }
}

